import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-attempted";

/**
 * Lazy import that survives stale deploys.
 * When a hashed chunk no longer exists (after a redeploy), retry once,
 * then force a single hard reload to pick up the new asset manifest.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (error) {
      // Retry once — transient network failures are common on mobile.
      try {
        const mod = await factory();
        sessionStorage.removeItem(RELOAD_KEY);
        return mod;
      } catch {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "true";
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_KEY, "true");
          window.location.reload();
          // Never resolves; the page is reloading.
          return new Promise<{ default: T }>(() => {});
        }
        throw error;
      }
    }
  });
}
